import React, { useState } from 'react';
import Card from 'react-bootstrap/Card';
import { styles } from '../styles/styles';
import { useHapticNavigate as useNavigate } from "../utils/haptics";
import { fmt, isNum, ratio } from "../utils/format.js";
import {
  CONVICTION_LEVELS, TIERS, comparePicks, getConviction, isV2, parsePick, resultColor, unitsText, unitsWon,
} from "../utils/picks.js";
import * as motion from "motion/react-client";

const isUpset = (text) => text.includes('(upset potential)');

const getSideOrTotal = (suggestion) => {
  if (suggestion.bet_type === 'total') return 'Total';
  // A draw is neither a side nor a total — it is the outcome where neither side
  // wins, so filing it under 'Side' would put it in the same bucket as backing a
  // team. Its own category, and the hit-rate breakdown counts it separately.
  if (suggestion.bet_type === 'draw') return 'Draw';
  return 'Side';
};

const getBetLabel = (suggestion) => {
  switch (suggestion.bet_type) {
    case 'moneyline': return 'ML';
    case 'spread': return 'SPREAD';
    case 'total': return 'TOTAL';
    case 'draw': return 'DRAW';
    default: return '';
  }
};

const getResultColor = (result) => {
  switch (result) {
    case 'hit': return '#4caf50';
    case 'loss': return '#FF6347';
    case 'push': return '#FFD700';
    default: return 'rgba(255,255,255,0.3)';
  }
};

const getResultLabel = (result) => {
  switch (result) {
    case 'hit': return 'HIT';
    case 'loss': return 'MISS';
    case 'push': return 'PUSH';
    default: return 'PENDING';
  }
};

const FilterButton = ({ label, active, onClick }) => (
  <button
    onClick={onClick}
    style={{
      backgroundColor: active ? 'white' : 'transparent',
      color: active ? 'black' : 'white',
      border: '1px solid rgba(255,255,255,0.4)',
      borderRadius: '16px',
      padding: '4px 12px',
      fontSize: '0.75rem',
      cursor: 'pointer',
      transition: 'all 0.2s',
    }}
  >
    {label}
  </button>
);

const MiniBar = ({ hits, total }) => {
  // A bar with nothing resolved yet is not a 0% hit rate, and must not render in
  // the failure colour. pct stays null until there is something to divide by.
  const pct = ratio(hits, total);
  if (pct === null) {
    return (
      <div style={{ display: 'flex', alignItems: 'center', gap: '8px', marginBottom: '4px' }}>
        <div style={{ flex: 1, height: '6px', backgroundColor: 'rgba(255,255,255,0.1)', borderRadius: '3px' }} />
        <span style={{ color: 'rgba(255,255,255,0.7)', fontSize: '0.7rem', minWidth: '70px', textAlign: 'right' }}>
          none yet
        </span>
      </div>
    );
  }
  return (
    <div style={{ display: 'flex', alignItems: 'center', gap: '8px', marginBottom: '4px' }}>
      <div style={{
        flex: 1,
        height: '6px',
        backgroundColor: 'rgba(255,255,255,0.1)',
        borderRadius: '3px',
        overflow: 'hidden',
      }}>
        <div style={{
          width: `${pct}%`,
          height: '100%',
          backgroundColor: pct > 50 ? '#4caf50' : '#FF6347',
          borderRadius: '3px',
          transition: 'width 0.5s ease',
        }} />
      </div>
      <span style={{ color: 'rgba(255,255,255,0.7)', fontSize: '0.7rem', minWidth: '70px', textAlign: 'right' }}>
        {hits}/{total} ({Math.round(pct)}%)
      </span>
    </div>
  );
};

const AccuracyDots = ({ suggestions }) => {
  // Show last N resolved suggestions as dots (most recent first)
  const resolved = suggestions.filter(s => s.result === 'hit' || s.result === 'loss');
  const recent = resolved.slice(0, 10);
  if (recent.length === 0) return null;

  return (
    <div style={{ display: 'flex', gap: '3px', alignItems: 'center' }}>
      <span style={{ color: 'rgba(255,255,255,0.5)', fontSize: '0.65rem', marginRight: '4px' }}>Recent:</span>
      {recent.map((s, i) => (
        <div
          key={i}
          style={{
            width: '8px',
            height: '8px',
            borderRadius: '50%',
            backgroundColor: s.result === 'hit' ? '#4caf50' : '#FF6347',
          }}
        />
      ))}
    </div>
  );
};

// v1's box, as it has always been (librarian B29 P2.6 keeps it byte-identical). On a
// slate with v2 plays it takes only the v1 plays (`select`), under "Earlier picks".
const LegacySuggestions = ({ fixtures, userTier, select = null, title = 'Top Suggestions' }) => {
  const navigate = useNavigate();
  const [filter, setFilter] = useState('all');

  if (userTier === 'Anon' || !userTier || !fixtures) {
    return null;
  }

  const allSuggestions = fixtures.flatMap(fixture =>
    (Array.isArray(fixture.suggestions) ? fixture.suggestions : []).map(suggestion => ({
      ...suggestion,
      fixture: fixture
    }))
  // Same defect as Cards/suggestions.js: `!== undefined` admits null, and the
  // .toFixed(2) at the render site throws. This copy is on the league slate.
  // A text that isn't a string would throw in isUpset, getConviction and the render.
  ).filter(item => typeof item.text === 'string' && item.text && isNum(item.score)
    && (!select || select(item)));

  const sortedSuggestions = allSuggestions.sort((a, b) => b.score - a.score);

  if (sortedSuggestions.length === 0) {
    return null;
  }

  // Apply filter
  const filteredSuggestions = sortedSuggestions.filter(s => {
    if (filter === 'hit') return s.result === 'hit';
    if (filter === 'loss') return s.result === 'loss';
    if (filter === 'upset') return isUpset(s.text);
    return true;
  });

  // Stats calculations (always on full set)
  const resolved = sortedSuggestions.filter(s => s.result === 'hit' || s.result === 'loss');
  const hitCount = resolved.filter(s => s.result === 'hit').length;
  const totalResolved = resolved.length;
  const pushCount = sortedSuggestions.filter(s => s.result === 'push').length;
  const pendingCount = sortedSuggestions.filter(s => !s.result).length;

  // By conviction
  const convictionStats = {};
  for (const level of CONVICTION_LEVELS) {
    const group = resolved.filter(s => getConviction(s.text) === level);
    if (group.length > 0) {
      convictionStats[level] = {
        hits: group.filter(s => s.result === 'hit').length,
        total: group.length,
      };
    }
  }

  // By type
  const sideResolved = resolved.filter(s => getSideOrTotal(s) === 'Side');
  const totalTypeResolved = resolved.filter(s => getSideOrTotal(s) === 'Total');
  const typeStats = {
    Side: { hits: sideResolved.filter(s => s.result === 'hit').length, total: sideResolved.length },
    Total: { hits: totalTypeResolved.filter(s => s.result === 'hit').length, total: totalTypeResolved.length },
  };

  // Upsets
  const upsetResolved = resolved.filter(s => isUpset(s.text));
  const upsetCount = sortedSuggestions.filter(s => isUpset(s.text)).length;

  const handleClick = (fixture) => {
    navigate(`/fixture/${fixture.id}`, { state: { fixture } });
  };

  return (
    <motion.div
      initial={{ opacity: 0, y: -20 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.5 }}
    >
      <Card style={{
        backgroundColor: 'black',
        marginBottom: '20px',
        border: '1px solid white'
      }}>
        <Card.Header style={{ color: 'white', borderBottom: '1px solid white' }}>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: '8px' }}>
            <h5 style={{ margin: 0 }}>{title}</h5>
            <div style={{ display: 'flex', gap: '6px', flexWrap: 'wrap' }}>
              <FilterButton label="All" active={filter === 'all'} onClick={() => setFilter('all')} />
              <FilterButton label="Hits" active={filter === 'hit'} onClick={() => setFilter('hit')} />
              <FilterButton label="Misses" active={filter === 'loss'} onClick={() => setFilter('loss')} />
              {upsetCount > 0 && (
                <FilterButton label="Upsets" active={filter === 'upset'} onClick={() => setFilter('upset')} />
              )}
            </div>
          </div>
        </Card.Header>
        <div style={{
          maxHeight: '50vh',
          overflowY: 'auto',
          scrollSnapType: 'y mandatory'
        }}>
          {filteredSuggestions.length === 0 ? (
            <div style={{ color: 'rgba(255,255,255,0.5)', textAlign: 'center', padding: '20px', fontSize: '0.85rem' }}>
              No {filter === 'hit' ? 'hits' : filter === 'loss' ? 'misses' : 'upsets'} to show
            </div>
          ) : (
            filteredSuggestions.map((suggestion, index) => (
              <motion.div
                key={index}
                initial={{ opacity: 0, x: -20 }}
                animate={{ opacity: 1, x: 0 }}
                transition={{ delay: index * 0.1 }}
                style={{ scrollSnapAlign: 'start' }}
              >
                <Card
                  onClick={() => handleClick(suggestion.fixture)}
                  style={{
                    backgroundColor: 'black',
                    border: 'none',
                    borderBottom: '1px solid rgba(255,255,255,0.2)',
                    cursor: 'pointer'
                  }}
                >
                  <Card.Body style={{ paddingBottom: '8px' }}>
                    <Card.Text style={{
                      ...styles.analysis,
                      color: resultColor(suggestion.result),
                      marginBottom: '6px',
                    }}>
                      {suggestion.text}
                    </Card.Text>
                    <div style={{ display: 'flex', alignItems: 'center', gap: '8px', flexWrap: 'wrap' }}>
                      <span style={{ color: 'white', fontSize: '0.8rem', opacity: 0.8 }}>
                        Score: {suggestion.score.toFixed(2)}
                      </span>
                      {suggestion.bet_type && (
                        <span style={{
                          fontSize: '0.65rem',
                          padding: '1px 6px',
                          borderRadius: '8px',
                          border: '1px solid rgba(255,255,255,0.3)',
                          color: 'rgba(255,255,255,0.6)',
                        }}>
                          {getBetLabel(suggestion)}
                        </span>
                      )}
                      <span style={{
                        fontSize: '0.65rem',
                        padding: '1px 6px',
                        borderRadius: '8px',
                        backgroundColor: `${getResultColor(suggestion.result)}22`,
                        color: getResultColor(suggestion.result),
                        border: `1px solid ${getResultColor(suggestion.result)}44`,
                      }}>
                        {getResultLabel(suggestion.result)}
                      </span>
                    </div>
                  </Card.Body>
                </Card>
              </motion.div>
            ))
          )}
        </div>
        <Card.Footer style={{
          color: 'white',
          borderTop: '1px solid rgba(255,255,255,0.2)',
          backgroundColor: 'black',
          padding: '12px 16px',
        }}>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '10px' }}>
            <div style={{ fontSize: '0.85rem' }}>
              Predictions: {totalResolved > 0 ? `${hitCount}/${totalResolved}` : 'none resolved yet'}
              {pushCount > 0 && <span style={{ color: '#FFD700', marginLeft: '8px' }}>{pushCount} push</span>}
              {pendingCount > 0 && <span style={{ color: 'rgba(255,255,255,0.4)', marginLeft: '8px' }}>{pendingCount} pending</span>}
            </div>
            <AccuracyDots suggestions={sortedSuggestions} />
          </div>

          <div style={{ display: 'flex', gap: '24px', flexWrap: 'wrap' }}>
            {/* By Conviction */}
            <div style={{ flex: 1, minWidth: '140px' }}>
              <div style={{ fontSize: '0.7rem', color: 'rgba(255,255,255,0.5)', marginBottom: '4px', textTransform: 'uppercase', letterSpacing: '0.5px' }}>
                By Conviction
              </div>
              {Object.entries(convictionStats).map(([level, { hits, total }]) => (
                <div key={level}>
                  <div style={{ fontSize: '0.7rem', color: 'rgba(255,255,255,0.6)', marginBottom: '1px' }}>{level}</div>
                  <MiniBar hits={hits} total={total} />
                </div>
              ))}
            </div>

            {/* By Type */}
            <div style={{ flex: 1, minWidth: '140px' }}>
              <div style={{ fontSize: '0.7rem', color: 'rgba(255,255,255,0.5)', marginBottom: '4px', textTransform: 'uppercase', letterSpacing: '0.5px' }}>
                By Type
              </div>
              {Object.entries(typeStats).filter(([, { total }]) => total > 0).map(([type, { hits, total }]) => (
                <div key={type}>
                  <div style={{ fontSize: '0.7rem', color: 'rgba(255,255,255,0.6)', marginBottom: '1px' }}>
                    {type === 'Side' ? 'Side (ML + Spread)' : 'Total (O/U)'}
                  </div>
                  <MiniBar hits={hits} total={total} />
                </div>
              ))}
              {upsetResolved.length > 0 && (
                <div>
                  <div style={{ fontSize: '0.7rem', color: 'rgba(255,255,255,0.6)', marginBottom: '1px' }}>Upsets</div>
                  <MiniBar hits={upsetResolved.filter(s => s.result === 'hit').length} total={upsetResolved.length} />
                </div>
              )}
            </div>
          </div>
        </Card.Footer>
      </Card>
    </motion.div>
  );
};

// v2's verdicts on the slate (librarian B29 P2.6): one play per market at Lean, Play or
// Strong, strongest first, the game's best bet starred, and results in units at each
// play's own price. v1's conviction bars become By Tier (v2 has no conviction label). Its
// type bars carry over as By Type (Brian, 2026-10-14): since librarian P2.10 every game's
// total is a pick, so the totals keep their own record. The draw is a market like any
// other, and gets its own row once one is graded.
const TIER_COLOR = { Strong: '#FFD700', Play: '#4caf50', Lean: '#9ecbff' };
const TYPE_NAME = { Side: 'Side (ML + Spread)', Total: 'Total (O/U)', Draw: 'Draw' };

const VerdictsBox = ({ fixtures, userTier }) => {
  const navigate = useNavigate();
  const [filter, setFilter] = useState('all');

  if (userTier === 'Anon' || !userTier || !fixtures) {
    return null;
  }

  const plays = fixtures.flatMap(fixture =>
    (Array.isArray(fixture.suggestions) ? fixture.suggestions : [])
      .filter(isV2)
      .map(play => ({ play, parsed: parsePick(play), fixture }))
  ).filter(({ parsed }) => parsed)
    .sort((a, b) => comparePicks(a.parsed, b.parsed));

  if (plays.length === 0) {
    return null;
  }

  const shown = plays.filter(({ play, parsed }) => {
    if (filter === 'hit') return play.result === 'hit';
    if (filter === 'loss') return play.result === 'loss';
    if (filter === 'best') return parsed.best;
    return true;
  });

  const resolved = plays.filter(({ play }) => play.result === 'hit' || play.result === 'loss');
  const hits = resolved.filter(({ play }) => play.result === 'hit').length;
  const pushCount = plays.filter(({ play }) => play.result === 'push').length;
  const pendingCount = plays.filter(({ play }) => !play.result).length;
  const won = plays.map(({ play }) => unitsWon(play)).filter(isNum);
  const units = won.length ? won.reduce((a, b) => a + b, 0) : null;
  const tierStats = TIERS.map(tier => {
    const group = resolved.filter(({ parsed }) => parsed.label === tier);
    return [tier, group.filter(({ play }) => play.result === 'hit').length, group.length];
  }).filter(([, , total]) => total > 0);
  const typeStats = Object.keys(TYPE_NAME).map(type => {
    const group = resolved.filter(({ play }) => getSideOrTotal(play) === type);
    return [type, group.filter(({ play }) => play.result === 'hit').length, group.length];
  }).filter(([, , total]) => total > 0);
  const bestCount = plays.filter(({ parsed }) => parsed.best).length;

  // The slate lists every game, so a total keeps its "A vs B": only the label goes.
  const pickOf = ({ play, parsed }) => (parsed.label && play.text.startsWith(`${parsed.label}: `)
    ? play.text.slice(parsed.label.length + 2) : play.text);

  return (
    <motion.div initial={{ opacity: 0, y: -20 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.5 }}>
      <Card className="verdicts-box" style={{ backgroundColor: 'black', marginBottom: '20px', border: '1px solid white' }}>
        <Card.Header style={{ color: 'white', borderBottom: '1px solid white' }}>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: '8px' }}>
            <h5 style={{ margin: 0 }}>Verdicts</h5>
            <div style={{ display: 'flex', gap: '6px', flexWrap: 'wrap' }}>
              <FilterButton label="All" active={filter === 'all'} onClick={() => setFilter('all')} />
              <FilterButton label="Hits" active={filter === 'hit'} onClick={() => setFilter('hit')} />
              <FilterButton label="Misses" active={filter === 'loss'} onClick={() => setFilter('loss')} />
              {bestCount > 0 && (
                <FilterButton label="Best bets" active={filter === 'best'} onClick={() => setFilter('best')} />
              )}
            </div>
          </div>
        </Card.Header>
        <div style={{ maxHeight: '50vh', overflowY: 'auto', scrollSnapType: 'y mandatory' }}>
          {shown.length === 0 ? (
            <div style={{ color: 'rgba(255,255,255,0.5)', textAlign: 'center', padding: '20px', fontSize: '0.85rem' }}>
              No {filter === 'hit' ? 'hits' : filter === 'loss' ? 'misses' : 'best bets'} to show
            </div>
          ) : (
            shown.map((item, index) => (
              <motion.div key={index} initial={{ opacity: 0, x: -20 }} animate={{ opacity: 1, x: 0 }}
                          transition={{ delay: index * 0.1 }} style={{ scrollSnapAlign: 'start' }}>
                <Card className="verdicts-play"
                      onClick={() => navigate(`/fixture/${item.fixture.id}`, { state: { fixture: item.fixture } })}
                      style={{ backgroundColor: 'black', border: 'none', borderBottom: '1px solid rgba(255,255,255,0.2)', cursor: 'pointer' }}>
                  <Card.Body style={{ paddingBottom: '8px' }}>
                    <Card.Text style={{ ...styles.analysis, color: resultColor(item.play.result), marginBottom: '6px' }}>
                      {pickOf(item)}{item.parsed.best ? ' ★' : ''}
                    </Card.Text>
                    <div style={{ display: 'flex', alignItems: 'center', gap: '8px', flexWrap: 'wrap' }}>
                      {item.parsed.label && (
                        <span style={{
                          fontSize: '0.7rem', padding: '1px 8px', borderRadius: '8px', fontWeight: 'bold',
                          backgroundColor: TIER_COLOR[item.parsed.label], color: 'black',
                        }}>
                          {[item.parsed.label, unitsText(item.parsed.units)].filter(Boolean).join(' · ')}
                        </span>
                      )}
                      {item.play.bet_type && (
                        <span style={{
                          fontSize: '0.65rem', padding: '1px 6px', borderRadius: '8px',
                          border: '1px solid rgba(255,255,255,0.3)', color: 'rgba(255,255,255,0.6)',
                        }}>
                          {getBetLabel(item.play)}
                        </span>
                      )}
                      <span style={{
                        fontSize: '0.65rem', padding: '1px 6px', borderRadius: '8px',
                        backgroundColor: `${getResultColor(item.play.result)}22`,
                        color: getResultColor(item.play.result),
                        border: `1px solid ${getResultColor(item.play.result)}44`,
                      }}>
                        {getResultLabel(item.play.result)}
                      </span>
                    </div>
                  </Card.Body>
                </Card>
              </motion.div>
            ))
          )}
        </div>
        <Card.Footer style={{ color: 'white', borderTop: '1px solid rgba(255,255,255,0.2)', backgroundColor: 'black', padding: '12px 16px' }}>
          <div style={{ fontSize: '0.85rem', marginBottom: '10px' }}>
            Verdicts: {resolved.length > 0 ? `${hits}/${resolved.length}` : 'none resolved yet'}
            {pushCount > 0 && <span style={{ color: '#FFD700', marginLeft: '8px' }}>{pushCount} push</span>}
            {pendingCount > 0 && <span style={{ color: 'rgba(255,255,255,0.4)', marginLeft: '8px' }}>{pendingCount} pending</span>}
            {units !== null && (
              <span className="verdicts-units" style={{ marginLeft: '8px', color: units >= 0 ? '#4caf50' : '#FF6347' }}>
                {fmt(units, 2, { signed: true, suffix: 'u' })}
              </span>
            )}
          </div>
          {(tierStats.length > 0 || typeStats.length > 0) && (
            <div style={{ display: 'flex', gap: '24px', flexWrap: 'wrap' }}>
              {tierStats.length > 0 && (
                <div className="verdicts-tiers" style={{ flex: 1, minWidth: '140px' }}>
                  <div style={{ fontSize: '0.7rem', color: 'rgba(255,255,255,0.5)', marginBottom: '4px', textTransform: 'uppercase', letterSpacing: '0.5px' }}>
                    By Tier
                  </div>
                  {tierStats.map(([tier, tierHits, total]) => (
                    <div key={tier}>
                      <div style={{ fontSize: '0.7rem', color: 'rgba(255,255,255,0.6)', marginBottom: '1px' }}>{tier}</div>
                      <MiniBar hits={tierHits} total={total} />
                    </div>
                  ))}
                </div>
              )}
              {typeStats.length > 0 && (
                <div className="verdicts-types" style={{ flex: 1, minWidth: '140px' }}>
                  <div style={{ fontSize: '0.7rem', color: 'rgba(255,255,255,0.5)', marginBottom: '4px', textTransform: 'uppercase', letterSpacing: '0.5px' }}>
                    By Type
                  </div>
                  {typeStats.map(([type, typeHits, total]) => (
                    <div key={type}>
                      <div style={{ fontSize: '0.7rem', color: 'rgba(255,255,255,0.6)', marginBottom: '1px' }}>{TYPE_NAME[type]}</div>
                      <MiniBar hits={typeHits} total={total} />
                    </div>
                  ))}
                </div>
              )}
            </div>
          )}
        </Card.Footer>
      </Card>
    </motion.div>
  );
};

// The slate-wide box. A slate with no v2 play renders exactly as before. With v2 plays,
// the verdicts come first and the v1 plays follow under "Earlier picks" (they last on
// slates from before the switch). No hooks here, so the v1 path's element tree is
// today's.
const SuggestionsBox = ({ fixtures, userTier }) => {
  const hasV2 = Array.isArray(fixtures)
    && fixtures.some(f => Array.isArray(f.suggestions) && f.suggestions.some(isV2));
  if (!hasV2) {
    return <LegacySuggestions fixtures={fixtures} userTier={userTier} />;
  }
  return (
    <>
      <VerdictsBox fixtures={fixtures} userTier={userTier} />
      <LegacySuggestions fixtures={fixtures} userTier={userTier} select={(s) => !isV2(s)} title="Earlier picks" />
    </>
  );
};

export default SuggestionsBox;
